import {
  practiceState,
  type ItemHelp,
  type HelpAction,
} from "../fuxi/yufa-practice.js";
import {
  againField,
  arrangedField,
  chosenField,
  itemField,
  typedField,
  versionField,
  revisionField,
  itemNameField,
  repairLocationField,
} from "../content/lianxi/exercise-fields.js";
import type { ItemAnswer } from "../fuxi/yufa-marking.js";
import { type HandoverCount, answerHandoverCount } from "./lianxi-handover.js";
import { ExercisePress, type RecordAnswer } from "./lianxi-post.js";
import type { YufaKeeper } from "./lianxi-yufa-keeper.js";
import type { KeptQueue } from "./lianxi-yufa-queue.js";

/**
 * Keeping a reader's place in a grammar exercise 语法练习 through the
 * `/user/lianxi/` handler.
 *
 * The handler's page for an exercise carries where a press posts, and this is
 * the press. Each answer is posted with the item it was on and what the
 * reader produced, the word typed into a gap or the pieces of an arrangement
 * in their order, and the handler marks it with the module the page marked
 * it with and keeps the outcome. Pressing Do the exercise again posts that.
 * Every answer carries the queue as the handler now holds it, and the
 * exercise adopts it. docs/lianxi.md under "Keeping a reader's place" has
 * the exchange, and `src/user/yufa-report.ts` is the other end.
 * `lianxi-recorder.ts` is the same for a writing drill.
 *
 * **A press that cannot reach the handler moves the page on its own
 * arithmetic.** The page marked the answer itself before posting it, so the
 * reader is never waiting on the handler, and the handler's copy catches up
 * on the next press that lands.
 *
 * **An answer says when the finish has handed the sentences to 复习**, and
 * the recorder remembers it for the finish line. The block says it on the
 * way in for an exercise finished before the page was opened.
 */
export class YufaRecorder implements YufaKeeper {
  readonly #press: ExercisePress;
  #handedOver: boolean;
  #handoverCount: HandoverCount | undefined;
  readonly #version: string | undefined;
  readonly #items: readonly string[];
  #revision: number | undefined;
  #commented: boolean;
  #pending: Promise<unknown> = Promise.resolve();

  /** Whether the reader holds no Pro 专业, which the finish line names. */
  readonly free: boolean;

  constructor(properties: {
    /** Where a press posts, as the block says. */
    readonly address: string;

    /** The exercise's slug, which every post names. */
    readonly slug: string;
    readonly fetch: typeof fetch;
    readonly version?: string | undefined;
    readonly revision?: number | undefined;
    readonly items?: readonly string[];
    readonly commented?: boolean;

    /** Whether the reader holds no Pro 专业, as the block said. */
    readonly free?: boolean | undefined;

    /** Whether the block said the sentences were handed over already. */
    readonly handedOver?: boolean | undefined;

    /** How many cards the block said the hand-over put in, and when due. */
    readonly handoverCount?: HandoverCount | undefined;
  }) {
    this.#press = new ExercisePress(properties);
    this.#handedOver = properties.handedOver ?? false;
    this.#handoverCount = properties.handoverCount;
    this.free = properties.free ?? false;
    this.#version = properties.version;
    this.#revision = properties.revision;
    this.#items = properties.items ?? [];
    this.#commented = properties.commented ?? false;
  }

  /** Whether this sitting already holds a comment. */
  get commented(): boolean {
    return this.#commented;
  }

  /** Whether the finish has handed the sentences to 复习, as far as any answer said. */
  get handedOver(): boolean {
    return this.#handedOver;
  }

  /** How many cards the hand-over put in and when the first is due, as last said. */
  get handoverCount(): HandoverCount | undefined {
    return this.#handoverCount;
  }

  /** An item answered, posted to be marked and kept. */
  async answered(
    item: number,
    answer: ItemAnswer,
    help?: ItemHelp,
  ): Promise<KeptQueue | undefined> {
    const kept = await this.#post({
      [itemField]: String(item),
      ...(this.#version === undefined
        ? {}
        : { [itemNameField]: this.#items[item] ?? "" }),
      ...answerFields(answer),
      ...(help === undefined
        ? {}
        : {
            helpHints: String(help.hints),
            answerSeen: String(help.answer),
            ...Object.fromEntries(
              (["listened", "transcript", "audioFallback"] as const)
                .filter((key) => help[key] !== undefined)
                .map((key) => [key, String(help[key])]),
            ),
          }),
    });

    return kept === undefined ? undefined : keptQueue(kept);
  }

  /** Keep help before a later answer can be recorded. */
  async helped(item: number, help: HelpAction): Promise<KeptQueue | undefined> {
    const kept = await this.#post({
      [itemField]: String(item),
      [itemNameField]: this.#items[item] ?? "",
      help,
    });
    return kept === undefined ? undefined : keptQueue(kept);
  }

  /** The exercise started again. */
  async again(): Promise<KeptQueue | undefined> {
    const kept = await this.#post({ [againField]: "" });
    return kept === undefined ? undefined : keptQueue(kept);
  }

  /** A sitting accepts one optional comment without consuming an exercise start. */
  async comment(comment: string): Promise<boolean> {
    return (await this.#post({ comment }))?.commentSaved === true;
  }

  /** One press, and what its answer said about the hand-over. */
  #post(
    fields: Readonly<Record<string, string>>,
  ): Promise<RecordAnswer | undefined> {
    const post = async (): Promise<RecordAnswer | undefined> => {
      const answer = await this.#press.post({
        ...fields,
        ...(this.#version === undefined
          ? {}
          : {
              [versionField]: this.#version,
              [revisionField]: String(this.#revision ?? 0),
            }),
      });
      if (this.#version !== undefined && answer?.version !== this.#version) {
        return undefined;
      }
      if (
        typeof answer?.revision === "number" &&
        Number.isInteger(answer.revision) &&
        answer.revision >= 0
      ) {
        this.#revision = answer.revision;
      }
      this.#handedOver = this.#handedOver || answer?.handedOver === true;
      if (answer !== undefined) {
        this.#commented = answer.commented === true;
      }
      this.#handoverCount = answerHandoverCount(answer) ?? this.#handoverCount;
      return answer;
    };
    const pending =
      this.#version === undefined ? post() : this.#pending.then(post);
    this.#pending = pending;
    return pending;
  }
}

/**
 * What an answer posts: the text typed into a gap or a Transform item, the
 * pieces of an arrangement one to a line, or the place of the sentence a
 * Choose item's reader picked.
 */
function answerFields(answer: ItemAnswer): Readonly<Record<string, string>> {
  if (answer.kind === "repair") {
    return {
      [repairLocationField]: String(answer.chosen),
      [typedField]: answer.typed,
    };
  }
  if (answer.kind === "arrange") {
    return { [arrangedField]: answer.arranged.join("\n") };
  }

  return answer.kind === "choose" ||
    answer.kind === "contrast" ||
    answer.kind === "placement" ||
    answer.kind === "interpretation" ||
    answer.kind === "reply"
    ? { [chosenField]: String(answer.chosen) }
    : { [typedField]: answer.typed };
}

/** Whether a value is a list of numbers. */
function isNumbers(value: unknown): value is readonly number[] {
  return Array.isArray(value) && value.every((at) => typeof at === "number");
}

/**
 * The queue an answer carries, or nothing where the answer is short of it.
 * @param answer - The handler's answer.
 * @returns The items waiting and the items missed.
 */
export function keptQueue(answer: RecordAnswer): KeptQueue | undefined {
  const { queue, missed } = answer;

  const practice = practiceState(answer.practice, Number.MAX_SAFE_INTEGER);
  return isNumbers(queue) && isNumbers(missed)
    ? { queue, missed, ...(practice === undefined ? {} : { practice }) }
    : undefined;
}
